import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useNavbarState } from '../../../hooks/useNavbarState';
import { useJournalSettings } from '../../../hooks/useJournalSettings';

/**
 * Warm Broadsheet's public nav chrome -- moved as-is from the pre-theming Navbar.tsx.
 * Consumes the same useNavbarState() hook every theme's Navbar does, so behavior/timing
 * is guaranteed identical across themes; only this markup is theme-specific.
 */
export function WarmBroadsheetNavbar({ topBarVisible }: { topBarVisible: boolean }) {
  const {
    isOpen,
    setIsOpen,
    scrolled,
    activeDropdown,
    mobileDropdownOpen,
    navItems,
    handleDropdownEnter,
    handleDropdownLeave,
    toggleMobileDropdown,
    isActive,
    isChildActive,
    isDropdownItemActive,
  } = useNavbarState();
  const settings = useJournalSettings();

  return (
    <nav
      className={`fixed w-full ${topBarVisible ? 'top-10 sm:top-10' : 'top-0'} transition-all duration-500 ${scrolled
        ? 'bg-vellum/98 backdrop-blur-md shadow-[0_2px_20px_-2px_rgba(44,24,16,0.08)] border-b border-dust'
        : 'bg-vellum border-b-2 border-espresso'
        } z-40`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 lg:h-[72px]">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="flex flex-col leading-none">
                {settings.settingsLoading ? (
                  <span className="inline-block h-7 lg:h-[28px] w-28 bg-dust/50 rounded animate-pulse" aria-hidden="true" />
                ) : (
                  <span className="font-display text-2xl lg:text-[28px] font-black text-espresso tracking-tight">
                    {settings.journalAbbreviation || settings.journalName}
                  </span>
                )}
              </div>
            </Link>
            <span className="hidden xl:inline-flex items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.07em] text-moss bg-moss-light border border-moss/30 rounded-sm px-2 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-moss"></span>
              Diamond Open Access
            </span>
          </div>

          <div className="hidden lg:flex items-center space-x-0.5">
            {navItems.map((item) => (
              item.children ? (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter(item.name)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button
                    className={`px-3 py-2.5 text-sm font-medium font-sans whitespace-nowrap transition-all duration-200 flex items-center gap-1.5 border-b-2 ${isActive(item.path) || isChildActive(item.children) || activeDropdown === item.name
                      ? 'text-brick border-brick'
                      : 'text-ink-2 border-transparent hover:text-espresso hover:border-dust'
                      }`}
                  >
                    {item.name}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                  </button>

                  {activeDropdown === item.name && (
                    <div
                      className="absolute top-full left-0 mt-1 w-72 bg-white rounded-md shadow-xl border border-dust py-2 animate-fadeIn"
                      onMouseEnter={() => handleDropdownEnter(item.name)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      {item.children.map((child, idx) => (
                        <Link
                          key={child.name}
                          to={child.path}
                          state={child.state}
                          className={`flex items-center gap-3 px-4 py-3 text-sm font-sans transition-all duration-150 ${isDropdownItemActive(child.path)
                            ? 'text-brick bg-brick-light'
                            : 'text-ink-2 hover:text-brick hover:bg-vellum'
                            } ${idx === 0 ? '' : 'border-t border-dust'}`}
                        >
                          {child.icon && (
                            <div className={`p-1.5 rounded-md ${isDropdownItemActive(child.path) ? 'bg-white' : 'bg-vellum-2'}`}>
                              <child.icon className={`h-4 w-4 ${isDropdownItemActive(child.path) ? 'text-brick' : 'text-ink-3'}`} />
                            </div>
                          )}
                          <span className="font-medium">{child.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : item.isCTA ? (
                <Link
                  key={item.name}
                  to={item.path}
                  className="ml-2 px-5 py-2.5 rounded-sm text-sm font-sans font-bold whitespace-nowrap transition-colors duration-200 flex items-center gap-2 bg-brick text-white hover:bg-brick-dark"
                >
                  {item.icon && <item.icon className="h-4 w-4" />}
                  {item.name}
                </Link>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-3 py-2.5 text-sm font-medium font-sans whitespace-nowrap transition-all duration-200 flex items-center border-b-2 ${isActive(item.path)
                    ? 'text-brick border-brick'
                    : 'text-ink-2 border-transparent hover:text-espresso hover:border-dust'
                    }`}
                >
                  {item.icon && <item.icon className="h-4 w-4 mr-1.5" />}
                  {item.name}
                </Link>
              )
            ))}
          </div>

          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-ink-2 hover:text-brick hover:bg-vellum-2 focus:outline-none transition-all duration-200"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden">
          <div className="px-3 pt-3 pb-4 space-y-1 bg-vellum border-t border-dust shadow-lg animate-fadeIn max-h-[calc(100vh-120px)] overflow-y-auto">
            {navItems.map((item) => (
              item.children ? (
                <div key={item.name} className="space-y-1">
                  <button
                    onClick={() => toggleMobileDropdown(item.name)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-md text-base font-sans font-medium transition-all duration-200 ${isActive(item.path) || isChildActive(item.children)
                      ? 'text-brick bg-brick-light'
                      : 'text-ink-2 hover:text-brick hover:bg-vellum-2'
                      }`}
                  >
                    <span>{item.name}</span>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${mobileDropdownOpen === item.name ? 'rotate-180' : ''}`} />
                  </button>

                  {mobileDropdownOpen === item.name && (
                    <div className="ml-4 pl-4 border-l-2 border-dust space-y-1 animate-fadeIn">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.path}
                          state={child.state}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-sans font-medium transition-all duration-200 ${isDropdownItemActive(child.path)
                            ? 'text-brick bg-brick-light'
                            : 'text-ink-2 hover:text-brick hover:bg-vellum-2'
                            }`}
                          onClick={() => {
                            setIsOpen(false);
                            toggleMobileDropdown(item.name);
                          }}
                        >
                          {child.icon && (
                            <div className={`p-1.5 rounded-md ${isDropdownItemActive(child.path) ? 'bg-white' : 'bg-vellum-2'}`}>
                              <child.icon className={`h-4 w-4 ${isDropdownItemActive(child.path) ? 'text-brick' : 'text-ink-3'}`} />
                            </div>
                          )}
                          <span>{child.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : item.isCTA ? (
                <Link
                  key={item.name}
                  to={item.path}
                  className="flex items-center justify-center px-4 py-3.5 mt-2 rounded-sm text-base font-sans font-bold transition-colors duration-200 bg-brick text-white hover:bg-brick-dark"
                  onClick={() => setIsOpen(false)}
                >
                  {item.icon && <item.icon className="h-5 w-5 mr-2" />}
                  {item.name}
                </Link>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-md text-base font-sans font-medium transition-all duration-200 ${isActive(item.path)
                    ? 'text-brick bg-brick-light'
                    : 'text-ink-2 hover:text-brick hover:bg-vellum-2'
                    }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.icon && <item.icon className="h-5 w-5 mr-3" />}
                  {item.name}
                </Link>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

export default WarmBroadsheetNavbar;
